import React from "react";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { FieldLabel, FieldSubLabel, FieldHelpText } from "./FieldParts";
import { cn } from "../lib/utils";
import { useElementValue, useIsReadOnly } from "../DocumentView/DocumentContext";
import { useDefaultValue } from "./useDefaultValue";
import HiddenElementBanner from "./HiddenElementBanner";
import ArchivedElementBanner from "./ArchivedElementBanner";

interface Props {
  element: DocumentElementType;
}

/**
 * Toggle-group input — wraps shadcn's ToggleGroup (Radix
 * primitive). Single-select. Reads its options from
 * `data.options: { value, label, icon? }[]`. Used mostly inside
 * config_form payloads (e.g. picking column alignment) but works
 * as a regular form input too.
 */
const ToggleGroup: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string | undefined>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    description?: string;
    helpText?: string;
    required?: boolean;
    hidden?: boolean;
    options?: Array<{ value: string; label?: string; icon?: string }>;
    defaultValue?: string;
  };
  const options = Array.isArray(data?.options) ? data!.options : [];
  const label = data?.label || "";
  const description = data?.description || "";
  const helpText = data?.helpText || "";
  const required = !!data?.required;
  // Hydrate the configured default into the answer like every other input,
  // so a required toggle group that shows a selection also holds one.
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue || undefined);

  // Read-only (e.g. a completed result): render the selected option's
  // label as static text — no operable toggle group.
  if (isReadOnly) {
    const selectedValue = displayValue ?? "";
    const selectedOption = options.find((o) => o.value === selectedValue);
    const selectedLabel = selectedOption
      ? selectedOption.label || selectedOption.value
      : "";
    return (
      <div className="space-y-2">
        {label && <FieldLabel required={required}>{label}</FieldLabel>}
        <FieldSubLabel>{description}</FieldSubLabel>
        {selectedLabel ? (
          <p className="text-sm">{selectedLabel}</p>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  const handleValueChange = (next: string) => {
    // Radix returns "" when the user clicks the active item again to
    // deselect. Persist that explicitly so a deselected default stays
    // deselected (undefined would re-hydrate the default on reload).
    setValue(next || "");
  };

  return (
    <div className="space-y-2">
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <FieldSubLabel>{description}</FieldSubLabel>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
      {options.length > 0 && (
        <div className="inline-flex w-full rounded-[11px] border border-[var(--forms-border-light)] bg-[#f1f3f6] p-[3px]">
          {options.map((option) => {
            const selected =
              (displayValue ?? "") === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={selected}
                onClick={() =>
                  handleValueChange(selected ? "" : option.value)
                }
                className={cn(
                  "flex-1 rounded-[8px] py-[9px] text-center text-[13px] font-bold transition-colors",
                  selected
                    ? "bg-white text-[var(--forms-accent,var(--forms-accent-default))] shadow-[0_1px_2px_rgba(16,24,40,.1)]"
                    : "text-[#5a6472] hover:text-[var(--forms-ink)]",
                )}
              >
                {option.label || option.value}
              </button>
            );
          })}
        </div>
      )}
      <FieldHelpText>{helpText}</FieldHelpText>
    </div>
  );
};

export default ToggleGroup;
